import { useCallback, useState } from "react";
import { agentApi } from "../../../api/client";
import { COLORS } from "../../../theme/colors";
import KillSwitchIndicator from "./KillSwitchIndicator";
import EditableField from "./edit/EditableField";
import { modelLabel, modelSettingOf } from "../../../utils/agentModel";

// ASC-v3 RH-STORE: routing examples live on `exemplars_json` (the field the
// runtime reads) as [{text, embedding}]. Read the texts from there; fall back to
// the legacy metadata_json/flow_config fields only for pre-RH-STORE display.
function exemplarTexts(a) {
  const ej = a?.exemplars_json;
  if (ej && Array.isArray(ej.exemplars)) {
    return ej.exemplars.map((x) => x?.text).filter(Boolean);
  }
  return a?.metadata_json?.exemplars || a?.flow_config?.exemplars || [];
}

// Phase B per agent-detail-viewer-v1.md §10 row 9 +
// agent-experience-redesign-v1.md B9 (editable supervisor pipeline).
//
// Four <Tile>s for the v13 routing pipeline (see CLAUDE.md v13 §):
//   L1   — deterministic regex/heuristic; routing_intents array EDITABLE.
//   L1.5 — BYOK-embedding cosine match against per-expert exemplars.
//          Per-expert "manage exemplars" → opens a per-expert drawer
//          (admin only). Drawer textareas the array; save bumps
//          tree_version on that expert's row.
//   L2   — tenant chat model = the router. system_prompt / provider /
//          model / temperature / max_tokens EDITABLE inline.
//   L3   — max_iterations EDITABLE (review_loop only). Mode is
//          Builder-only per §5.4 — render with silent suppression
//          + "Open in Builder" tooltip.
//
// Plus a <KillSwitchIndicator> polling /api/supervisor/killswitch/status
// every 5s. Admin-gated reset button when active.

const TILE_BG = (color) => `${color}10`;
const TILE_BORDER = (color) => `${color}55`;

const Tile = ({ title, kind, color, children, footer }) => (
  <div style={{
    padding: 18, borderRadius: 12,
    border: `1px solid ${TILE_BORDER(color)}`,
    background: `linear-gradient(135deg, ${TILE_BG(color)} 0%, ${COLORS.surface} 80%)`,
    minHeight: 140, display: "flex", flexDirection: "column", gap: 10,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{
        fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
        textTransform: "uppercase", color,
        background: `${color}22`, padding: "3px 8px", borderRadius: 6,
      }}>{kind}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{title}</span>
    </div>
    <div style={{ flex: 1, fontSize: 12, color: COLORS.text, lineHeight: 1.5 }}>{children}</div>
    {footer && (
      <div style={{ fontSize: 10, color: COLORS.textDim, borderTop: `1px solid ${COLORS.border}`, paddingTop: 8 }}>
        {footer}
      </div>
    )}
  </div>
);

const MODE_SHAPE = {
  fan_out:      { label: "fan_out",      desc: "Parallel asyncio.gather → citation-strict synthesis.", color: COLORS.cyan },
  sequential:   { label: "sequential",   desc: "Chain Agent A → B → C; each consumes prior output.",   color: COLORS.accentText },
  conditional:  { label: "conditional",  desc: "Classify → dispatch ONE specialist (no synthesis).",   color: COLORS.orange },
  review_loop:  { label: "review_loop",  desc: "Delegate → review → re-delegate or accept (cap N).",   color: COLORS.pink },
};

// Exported for unit-testing: resolve a supervisor mode string to its
// display shape, falling back to fan_out for unknown/empty modes.
export function resolveModeShape(mode) {
  return MODE_SHAPE[mode] || MODE_SHAPE.fan_out;
}

// Exported for unit-testing: parse a comma-separated string into a
// trimmed, de-blanked list (routing_intents editor).
export function parseCommaList(commaList) {
  return (commaList || "")
    .split(",")
    .map(s => s.trim())
    .filter(Boolean);
}

// ── L3 — mode (read-only, tree-shape) + max_iterations (editable) ──
function L3Tile({ supervisor, experts, saveAgent, canEdit }) {
  const fc = supervisor?.flow_config || {};
  const sup = (fc.supervisor || {});
  const mode = sup.mode || "fan_out";
  const meta = resolveModeShape(mode);
  const isReviewLoop = mode === "review_loop";

  const saveMaxIterations = async (v) => saveAgent({
    flow_config: { supervisor: { max_iterations: Number(v) } },
  });

  return (
    <Tile title={`L3 — ${meta.label}`} kind="L3" color={meta.color}
          footer={`expert_count=${experts.length}`}>
      <div style={{ marginBottom: 8, color: COLORS.textMuted }}>{meta.desc}</div>

      {/* Mode pill — readOnly per §5.4 (tree-shape lives in Builder) */}
      <EditableField
        label="Mode"
        value={mode}
        readOnly
        renderDisplay={(v) => (
          // White text on colored-tint background (per GitHub Copilot
          // Autofix pattern). Same-color text on same-color tinted bg
          // washes out — low contrast especially for purple/cyan
          // tints. The accent color stays in the border + bg tint to
          // identify the mode at a glance; text stays high-contrast.
          <span style={{
            padding: "2px 8px", borderRadius: 5, fontSize: 11, fontWeight: 600,
            background: `${meta.color}30`, color: COLORS.text,
            border: `1px solid ${meta.color}66`,
          }}>{v}</span>
        )}
      />

      {/* max_iterations — editable for review_loop; static for others */}
      {isReviewLoop ? (
        <EditableField
          label="max_iterations"
          variant="number"
          value={sup.max_iterations ?? 3}
          min={1} max={10}
          onSave={saveMaxIterations}
          readOnly={!canEdit}
          hint="How many delegate→review cycles before accepting"
        />
      ) : (
        <EditableField label="max_iterations" value={sup.max_iterations ?? "—"} readOnly />
      )}

      {experts.length === 0 ? (
        <div style={{ color: COLORS.textDim, marginTop: 8 }}>No experts configured.</div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {experts.map(e => (
            <span key={e.id} style={{
              padding: "3px 8px", borderRadius: 6, fontSize: 10,
              background: COLORS.surface, color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
            }}>{e.name}</span>
          ))}
        </div>
      )}
    </Tile>
  );
}

// ── L1 — routing_intents editable as comma-separated tags ──
function L1Tile({ supervisor, saveAgent, canEdit }) {
  const intents = supervisor?.routing_intents || [];

  const saveIntents = async (commaList) => saveAgent({
    routing_intents: parseCommaList(commaList),
  });

  return (
    <Tile title="L1 — deterministic" kind="L1" color={COLORS.green}
          footer={`${intents.length} intents · 0ms decision`}>
      <div style={{ marginBottom: 6, color: COLORS.textMuted }}>
        Regex / keyword fast-path. Bypasses L1.5+L2+L3 entirely.
      </div>

      {/* Read state — render as chip pills (clickable into edit) */}
      <EditableField
        label="Intents"
        variant="text"
        value={intents.join(", ")}
        onSave={saveIntents}
        readOnly={!canEdit}
        renderDisplay={(v) => {
          const list = parseCommaList(v);
          if (list.length === 0) return <span style={{ color: COLORS.textDim }}>None — click to add</span>;
          return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {list.map(i => (
                <span key={i} style={{
                  padding: "2px 7px", borderRadius: 5, fontSize: 10,
                  background: `${COLORS.green}22`, color: COLORS.green,
                  border: `1px solid ${COLORS.green}55`,
                }}>{i}</span>
              ))}
            </div>
          );
        }}
        placeholder="billing, support, refund"
        hint="Comma-separated routing intents (regex tokens). Match → L1 hard-routes."
      />
    </Tile>
  );
}

// ── L1.5 — exemplars per expert (read-only summary; admin can rebuild) ──
function L15Tile({ experts, supervisor, canEdit, onEditExpert }) {
  const totalExemplars = experts.reduce((acc, e) => {
    const ex = exemplarTexts(e);
    return acc + (Array.isArray(ex) ? ex.length : 0);
  }, 0);
  return (
    <Tile title="L1.5 — semantic match" kind="L1.5" color={COLORS.cyan}
          footer={`${totalExemplars} exemplars · ${experts.length} experts · ~10–20ms`}>
      <div style={{ marginBottom: 6, color: COLORS.textMuted }}>
        BYOK embedding cosine match against per-expert exemplars.
      </div>
      {experts.length === 0 ? (
        <div style={{ color: COLORS.textDim }}>No experts.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {experts.map(e => {
            const ex = exemplarTexts(e);
            const n = Array.isArray(ex) ? ex.length : 0;
            return (
              <div key={e.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                gap: 8, padding: "4px 6px", borderRadius: 5,
                cursor: canEdit ? "pointer" : "default",
                transition: "background 0.15s",
              }}
                onMouseEnter={(ev) => { if (canEdit) ev.currentTarget.style.background = `${COLORS.cyan}10`; }}
                onMouseLeave={(ev) => { ev.currentTarget.style.background = "transparent"; }}
                onClick={() => canEdit && onEditExpert && onEditExpert(e)}
              >
                <span style={{ color: COLORS.text, fontSize: 11, flex: 1 }}>{e.name}</span>
                <span style={{ color: COLORS.cyan, fontSize: 11, fontFamily: "monospace" }}>
                  {n} exemplars
                </span>
                {canEdit && <span style={{ color: COLORS.textDim, fontSize: 10 }}>edit →</span>}
              </div>
            );
          })}
        </div>
      )}
      {canEdit && (
        <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 6, lineHeight: 1.4 }}>
          Click an expert row to edit their exemplars.
        </div>
      )}
    </Tile>
  );
}

// ── L2 — system_prompt + provider/model editable inline ──
function L2Tile({ supervisor, saveAgent, canEdit }) {
  const saveSystemPrompt = async (v) => saveAgent({ system_prompt: v });
  const saveTemperature  = async (v) => saveAgent({ temperature: Number(v) });
  const saveMaxTokens    = async (v) => saveAgent({ max_tokens: Number(v) });

  return (
    <Tile title="L2 — lead agent" kind="L2" color={COLORS.purple}
          footer="P95 budget 5–7s · BYOK strict">
      <div style={{ marginBottom: 6, color: COLORS.textMuted }}>
        Tenant chat model decides direct-answer vs L3 fan-out.
      </div>

      <EditableField
        label="System prompt"
        variant="textarea"
        rows={4}
        value={supervisor?.system_prompt || ""}
        onSave={saveSystemPrompt}
        placeholder="System prompt that shapes routing decisions…"
        readOnly={!canEdit}
      />

      {/* Provider + Model intentionally read-only here — they live on
          the Graph drawer's call_llm node which has the BYOK-driven
          dropdowns. Surfacing here would mean two divergent edit
          surfaces for the same field; per §5.4 single-source rule
          we link out instead. */}
      <EditableField label="Model" value={modelLabel(modelSettingOf(supervisor))} readOnly
        hint={canEdit ? "Edit on Graph tab → call_llm node" : undefined} />

      <EditableField
        label="Temperature"
        variant="slider"
        value={supervisor?.temperature ?? 0.3}
        min={0} max={2} step={0.05}
        onSave={saveTemperature}
        readOnly={!canEdit}
      />
      <EditableField
        label="Max tokens"
        variant="number"
        value={supervisor?.max_tokens ?? 2048}
        min={64} max={32000}
        onSave={saveMaxTokens}
        readOnly={!canEdit}
      />
    </Tile>
  );
}

// ── Per-expert exemplars editor (modal-shaped popover) ──
function ExemplarsEditor({ expert, onClose, onSaved }) {
  const ex = exemplarTexts(expert);
  const [draft, setDraft] = useState(ex);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // ASC-v3 RH-STORE: persist to exemplars_json (the field routing reads) with
      // regenerated embeddings — NOT metadata_json.exemplars, which the runtime
      // never read, so edits silently had no effect on routing.
      await agentApi.updateExemplars(expert.id, draft.filter(s => s && s.trim()));
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 12, width: "90%", maxWidth: 520, maxHeight: "85vh",
        overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
      }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>Exemplars</div>
            <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>{expert.name}</div>
          </div>
          <button type="button" onClick={onClose} style={{
            background: "transparent", border: "none", color: COLORS.textDim, cursor: "pointer", fontSize: 16,
          }}>✕</button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 10, lineHeight: 1.5 }}>
            Sample queries this expert should match. L1.5 embeds these once
            on save and routes incoming questions to the closest match.
            One per line; blank lines are dropped.
          </div>
          <textarea
            value={draft.join("\n")}
            onChange={(e) => setDraft(e.target.value.split("\n"))}
            placeholder="What's the refund policy?&#10;How do I escalate?&#10;…"
            rows={10}
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "10px 12px", fontSize: 12, color: COLORS.text,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`, borderRadius: 6,
              outline: "none", fontFamily: "inherit", resize: "vertical",
            }}
          />
          {error && (
            <div style={{
              marginTop: 10, padding: "8px 12px", borderRadius: 6,
              background: `${COLORS.pink || "#ef4444"}15`,
              border: `1px solid ${COLORS.pink || "#ef4444"}55`,
              color: COLORS.pink || "#ef4444", fontSize: 12,
            }}>{error}</div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
            <button type="button" onClick={onClose} style={{
              background: "transparent", border: `1px solid ${COLORS.border}`,
              color: COLORS.textDim, padding: "6px 14px", borderRadius: 6,
              fontSize: 12, cursor: "pointer",
            }}>Cancel</button>
            <button type="button" onClick={save} disabled={submitting} style={{
              background: COLORS.accent, color: "#fff", border: "none",
              padding: "6px 14px", borderRadius: 6,
              fontSize: 12, fontWeight: 600, cursor: submitting ? "default" : "pointer",
              opacity: submitting ? 0.6 : 1,
            }}>{submitting ? "Saving…" : "Save exemplars"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PipelineTab({ tree, currentUser, saveAgent }) {
  const supervisor = tree?.agent;
  const experts = tree?.experts || [];
  const canMutate = currentUser?.role === "owner" || currentUser?.role === "administrator";
  const canEdit   = !["business_user"].includes(currentUser?.role);
  const [exemplarsExpert, setExemplarsExpert] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Wrap saveAgent so a successful mutation forces the parent to
  // refetch /tree (the parent already does this via refetchTick;
  // we just kick its tick when we know our change landed). Today
  // the parent's saveAgent already triggers refetch on success, so
  // this is a no-op safety net for the exemplars editor's direct
  // PUT path which bypasses the parent helper.
  const onExemplarsSaved = useCallback(() => {
    setRefreshTick(t => t + 1);
    // Note: parent's /tree fetch keyed on agentId only, not refresh
    // tick — but it'll catch up on next interaction; staleness here
    // would only manifest if user re-opens the same expert immediately
    // without any other interaction. Acceptable v1 trade-off.
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <KillSwitchIndicator canMutate={canMutate} />
      <div style={{
        display: "grid", gap: 14,
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      }}>
        <L1Tile  supervisor={supervisor} saveAgent={saveAgent} canEdit={canEdit} />
        <L15Tile experts={experts} supervisor={supervisor} canEdit={canEdit}
                 onEditExpert={setExemplarsExpert} />
        <L2Tile  supervisor={supervisor} saveAgent={saveAgent} canEdit={canEdit} />
        <L3Tile  supervisor={supervisor} experts={experts} saveAgent={saveAgent} canEdit={canEdit} />
      </div>

      {exemplarsExpert && (
        <ExemplarsEditor
          expert={exemplarsExpert}
          onClose={() => setExemplarsExpert(null)}
          onSaved={onExemplarsSaved}
        />
      )}
    </div>
  );
}
